// src/api.js
import axios from "axios";

// Backend base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "",
  withCredentials: true, // send cookies for session auth
});

// No JWT or special headers required now – cookie carries the session
api.interceptors.request.use((config) => config);

// Let callers handle 401 / errors
api.interceptors.response.use(
  (res) => res,
  (err) => Promise.reject(err)
);

// -------------------------------------------------------------------
// Auth
// -------------------------------------------------------------------

export async function login(email, password) {
  const r = await api.post("/login", { email, password });
  // Backend returns: { access_token, token_type, must_change_password, ... }
  return r.data;
}

// Exchange an Entra access token for the ff_session cookie.
export async function loginEntra(accessToken) {
  const r = await api.post("/login/entra", null, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return r.data;
}

export async function changePassword(currentPassword, newPassword) {
  const r = await api.post("/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return r.data;
}

export async function logout() {
  const r = await api.post("/logout");
  return r.data;
}

// -------------------------------------------------------------------
// Organizations
// -------------------------------------------------------------------

export async function getOrgs() {
  const r = await api.get("/organizations");
  return r.data || [];
}

// Optional helper used by OrgList.js
export async function addOrg(name, connectionUrl) {
  const payload = { name, connection_url: connectionUrl };
  const { data } = await api.post("/organizations", payload);
  return data;
}

// -------------------------------------------------------------------
// Service versions (org-first header strip)
// -------------------------------------------------------------------

// GET /organizations/{id}/versions
// Returns { org_id, org_name, available, error, versions: [...] }.
// The backend answers 200 with available=false when a tenant DB is
// unreachable or has no release_versions table, so only a real failure
// (404, 401, network) rejects here.
export async function getOrgVersions(orgId) {
  const r = await api.get(`/organizations/${orgId}/versions`);
  return r.data || null;
}

// -------------------------------------------------------------------
// Flags (org-first view)
// -------------------------------------------------------------------

export async function getFlags(orgId, search = "") {
  if (orgId === undefined || orgId === null) {
    return [];
  }

  // Backend requires org_ids (CSV)
  const params = { org_ids: String(orgId) };

  // Optional search wiring (adjust to match backend if needed)
  if (search && search.trim()) {
    params.feature_names = search.trim();
  }

  const r = await api.get("/features", { params });
  const data = r.data || {};

  // New backend shape: { features: [...] }
  if (Array.isArray(data.features)) {
    return data.features;
  }

  // Backward-compat fallback if feature_states exists
  const states = data.feature_states || {};
  const byId =
    states[orgId] ||
    states[String(orgId)] ||
    [];

  return Array.isArray(byId) ? byId : [];
}

/**
 * Turn a reported write failure into a thrown error, without losing what
 * succeeded.
 *
 * Both update endpoints answer 200 even when some tenant could not be written
 * to, because across several organizations a partial success is a real
 * outcome. Callers ignored the response body, so a failed write looked exactly
 * like a successful one: the optimistic cell stayed flipped and nothing was
 * said. Throwing lets their existing rollback-and-report paths run.
 *
 * The full payload rides on the error. Not every caller writes to a single
 * organization — the bulk path submits several at once — so a bare message
 * would throw away the organizations that did succeed and the per-org detail
 * needed to refresh them. `uncertain` matters most: it marks a write whose
 * commit was lost, which may or may not have applied.
 */
function throwIfWriteFailed(data) {
  const failed = (data && data.failed) || [];
  if (!failed.length) return;
  const detail = failed
    .map((f) => `${f.org_name || f.org_id}: ${f.error}`)
    .join("; ");
  const err = new Error(detail);
  err.writeFailed = failed;
  const results = (data && data.results) || [];
  err.writeResults = results;
  // Computed from everything, not just the failures. Counts for the orgs that
  // SUCCEEDED live outside `failed` entirely, and /features/update reports its
  // total at the top level, so looking only at `failed` reported partial:false
  // for a run where one org succeeded and another did not.
  const countedWritten =
    (typeof data?.updated === "number" ? data.updated : 0) +
    results.reduce((n, r) => n + (r.updated || 0), 0) +
    failed.reduce((n, f) => n + (f.updated || 0), 0);
  err.uncertain = failed.some((f) => f.uncertain);
  err.partial = err.uncertain || countedWritten > 0;
  err.data = data;
  throw err;
}

// Bulk update flags for org(s) via /features/update
export async function updateFlags(orgIds, featureNames, enabled) {
  const payload = {
    org_ids: orgIds,
    feature_names: featureNames,
    enabled,
  };
  const r = await api.post("/features/update", payload);
  throwIfWriteFailed(r.data);
  return r.data;
}


// -------------------------------------------------------------------
// Feature-first helpers (matrix + global feature list)
// -------------------------------------------------------------------

// Return a sorted array of feature names as strings
export async function listAllFeatures() {
  const r = await api.get("/features");
  const data = r.data || {};
  const features = data.features || [];

  if (!Array.isArray(features)) return [];

  return features
    .map((f) =>
      typeof f === "string" ? f : f.feature_name || f.name || null
    )
    .filter(Boolean)
    .sort();
}

// Low-level matrix fetch (orgIds & featureNames are arrays)
export async function getMatrix(orgIds, featureNames) {
  const params = {};

  if (orgIds && orgIds.length) {
    params.org_ids = orgIds.join(",");
  }
  if (featureNames && featureNames.length) {
    params.feature_names = featureNames.join(",");
  }

  const r = await api.get("/matrix", { params });
  return r.data;
}

// High-level helper used by FeatureFirstTab: fetch matrix for a single feature
export async function listFeatureMatrix(featureName) {
  const params = {};
  if (featureName) {
    params.feature_names = featureName;
  }
  const r = await api.get("/matrix", { params });
  const data = r.data || {};
  // Backend returns FeatureOrgStatusList: { items: [...] }
  return data && Array.isArray(data.items) ? data : { items: [] };
}

// Matrix-wide bulk update
// Be flexible:
//  - bulkUpdateMatrix([1,2], ["FLAG_A","FLAG_B"], true)
//  - bulkUpdateMatrix([1,2], [{ feature_name: "FLAG_A", enabled: true }, ...])
export async function bulkUpdateMatrix(orgIds, updatesOrFeatures, enabled) {
  let updates = [];

  if (Array.isArray(updatesOrFeatures) && updatesOrFeatures.length) {
    const first = updatesOrFeatures[0];

    if (typeof first === "object" && first.feature_name !== undefined) {
      // Already in [{ feature_name, enabled }, ...] form
      updates = updatesOrFeatures;
    } else {
      // Assume it's a list of feature names + single enabled flag
      updates = updatesOrFeatures.map((name) => ({
        feature_name: name,
        enabled: !!enabled,
      }));
    }
  }

  const payload = {
    org_ids: orgIds,
    updates,
  };

  const r = await api.post("/matrix/update", payload);
  throwIfWriteFailed(r.data);
  return r.data;
}

// Used by FeatureFirstTab:
// bulkUpdateFeature(feature, [orgId], enabled)
export async function bulkUpdateFeature(featureName, orgIds, enabled) {
  return bulkUpdateMatrix(orgIds, [
    { feature_name: featureName, enabled: !!enabled },
  ]);
}

// Default export so `import api from "./api"` works
export default api;
